import React, { useState, useEffect } from "react";

// React Router Dom
import { useNavigate } from "react-router-dom";

// Supabase
import { supabase } from "../../supabase";

// Hooks
import useSnackbarNotification from "../../hooks/useSnackbarNotification";

// Utils
import { getFriendlyErrorMessage } from "../../utils/errorHandling";

const ResetPassword = () => {
    const navigate = useNavigate();
    const { showError, showSuccess } = useSnackbarNotification();
    
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionEstablished, setSessionEstablished] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);

    // Handle recovery token from URL hash on mount
    useEffect(() => {
        let authListener = null;
        let timeoutId = null;
        
        const handleRecoverySession = async () => {
            try {
                // Set up auth state listener to catch PASSWORD_RECOVERY event
                authListener = supabase.auth.onAuthStateChange(
                    async (event, session) => {
                        if (event === 'PASSWORD_RECOVERY') {
                            // Supabase has automatically established the session
                            setSessionEstablished(true);
                            setCheckingSession(false);
                            // Clear the hash from URL for cleaner UX
                            window.history.replaceState(null, '', window.location.pathname);
                        } else if (event === 'SIGNED_IN' && session) {
                            // Check if this sign-in was from a recovery link
                            const hash = window.location.hash;
                            if (hash && hash.includes('type=recovery')) {
                                setSessionEstablished(true);
                                setCheckingSession(false);
                                window.history.replaceState(null, '', window.location.pathname);
                            }
                        }
                    }
                );

                // Check for hash parameters (Supabase sends tokens in URL hash for SPAs)
                const hash = window.location.hash;
                
                if (hash) {
                    // Parse hash parameters
                    const hashParams = new URLSearchParams(hash.substring(1));
                    const accessToken = hashParams.get('access_token');
                    const refreshToken = hashParams.get('refresh_token');
                    const type = hashParams.get('type');

                    // If we have recovery tokens, establish session manually
                    if (accessToken && refreshToken && type === 'recovery') {
                        const { data, error } = await supabase.auth.setSession({
                            access_token: accessToken,
                            refresh_token: refreshToken,
                        });

                        if (error) {
                            console.error('Error setting recovery session:', error);
                            showError('Invalid or expired reset link. Please request a new password reset.');
                            setCheckingSession(false);
                            return;
                        }

                        // Session established successfully
                        setSessionEstablished(true);
                        setCheckingSession(false);
                        // Clear the hash from URL for cleaner UX
                        window.history.replaceState(null, '', window.location.pathname);
                    } else {
                        // Hash exists but doesn't have recovery tokens
                        // Wait a moment for auth state change event, then check session
                        timeoutId = setTimeout(async () => {
                            const { data: { session } } = await supabase.auth.getSession();
                            if (session) {
                                setSessionEstablished(true);
                            } else {
                                showError('No valid reset session found. Please request a new password reset link.');
                            }
                            setCheckingSession(false);
                        }, 1500);
                    }
                } else {
                    // No hash, check for existing session (user might have refreshed page)
                    const { data: { session } } = await supabase.auth.getSession();
                    if (session) {
                        // Allow password reset if session exists (might be recovery session)
                        setSessionEstablished(true);
                        setCheckingSession(false);
                    } else {
                        showError('No valid reset session found. Please request a new password reset link.');
                        setCheckingSession(false);
                    }
                }
            } catch (error) {
                console.error('Error handling recovery session:', error);
                showError('An error occurred while processing the reset link. Please try again.');
                setCheckingSession(false);
            }
        };

        handleRecoverySession();

        // Cleanup
        return () => {
            if (authListener?.data?.subscription) {
                authListener.data.subscription.unsubscribe();
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [showError]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!sessionEstablished) {
            showError("Session not established. Please use the password reset link from your email.");
            return;
        }

        setLoading(true);

        // Client-side validation
        const trimmedPassword = password.trim();
        const trimmedConfirmPassword = confirmPassword.trim();

        if (!trimmedPassword) {
            showError("Please enter a new password.");
            setLoading(false);
            return;
        }

        if (!trimmedConfirmPassword) {
            showError("Please confirm your new password.");
            setLoading(false);
            return;
        }

        if (trimmedPassword !== trimmedConfirmPassword) {
            showError("Passwords do not match. Please make sure both passwords are the same.");
            setLoading(false);
            return;
        }

        // Password length validation
        if (trimmedPassword.length < 6) {
            showError("Password must be at least 6 characters long.");
            setLoading(false);
            return;
        }

        try {
            // Verify session is still valid before updating password
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                throw new Error("Session expired. Please request a new password reset link.");
            }

            const { error } = await supabase.auth.updateUser({
                password: trimmedPassword,
            });

            if (error) throw error;

            showSuccess("Password has been reset successfully!");
            // Sign out after password reset for security
            await supabase.auth.signOut();
            navigate("/login");
        } catch (error) {
            console.error("Password reset error:", error);
            // Use standard error handling - show account doesn't exist if applicable
            const friendlyMessage = getFriendlyErrorMessage(error, { 
                isPasswordReset: true 
            });
            showError(friendlyMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden md:flex md:w-1/2 bg-gray-50 flex-col space-y-6 overflow-auto">
                <img
                    src="/login.jpg"
                    alt="Reset Password"
                    className="w-full h-full object-cover"
                />
            </div>

            <div className="w-full md:w-1/2 bg-gray-50 flex flex-col justify-center px-8 md:px-20">
                <div className="max-w-md w-full mx-auto">
                    
                    <div
                        className="logo cursor-pointer justify-center mb-8"
                        onClick={() => navigate("/")}
                    >
                        Doctors' Jobs <span className="logo-badge">Beta</span>
                    </div>

                    <h2 className="text-2xl font-bold text-black text-center mb-8">
                        Reset Password
                    </h2>
                    <p className="text-sm text-black text-center mb-8">
                        Please enter your new password below.
                    </p>

                    {checkingSession && (
                        <div className="text-center mb-4">
                            <p className="text-sm text-gray-600">Verifying reset link...</p>
                        </div>
                    )}

                    {!checkingSession && !sessionEstablished && (
                        <div className="text-center mb-4">
                            <p className="text-sm text-red-600 mb-4">
                                No valid reset session found. Please request a new password reset link.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate("/forgot-password")}
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                                Request New Reset Link
                            </button>
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label
                                htmlFor="password"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                New Password *
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="confirmPassword"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Confirm New Password *
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading || !sessionEstablished || checkingSession}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                            >
                                {loading ? "Resetting..." : "Reset Password"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
